/**
 * PATH: packages/email-hub/src/feature/components/EmailReader.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENTS-EMAILREADER-001
 * PURPOSE: Assemble the canonical full email reader from typed modules.
 * WHY: Tenants consume this stable default and named-prop export surface.
 *      BUG-2026-07-17-EMAIL-CONTEXTMENU-COVERAGE — mount the same
 *      EmailContextMenuProvider the inbox/folder shells use so full-page
 *      and compose-side readers get Reply/Archive/etc on right-click.
 */
import { EmailContextMenuProvider } from './EmailContextMenu'
import { ReaderContent } from './email-reader/ReaderContent'
import { FullReaderWholeHeader } from './email-reader/FullReaderWholeHeader'
import { ReaderHeader } from './email-reader/ReaderHeader'
import { ReaderOverlays } from './email-reader/ReaderOverlays'
import {
  ReaderEmptyState,
  ReaderErrorState,
  ReaderLoadingState,
} from './email-reader/ReaderStates'
import { useEmailReaderController } from './email-reader/useEmailReaderController'
import type { EmailReaderProps } from './email-reader/types'
import { isEmailHubFeatureEnabled } from '../../_internal/flags'

export type { EmailReaderProps } from './email-reader/types'

export default function EmailReader(props: EmailReaderProps) {
  const controller = useEmailReaderController(props)
  const wholeHeaderEnabled = isEmailHubFeatureEnabled('wholeReaderHeader')
  const {
    emailId,
    email,
    loading,
    error,
    onBack,
    backLabel,
    refetch,
    allocationMembers,
  } = controller

  if (!emailId) return <ReaderEmptyState />
  if (loading && !email) return <ReaderLoadingState />
  if (error || !email) {
    return (
      <ReaderErrorState
        error={error}
        onBack={onBack}
        backLabel={backLabel}
      />
    )
  }

  return (
    <EmailContextMenuProvider
      onRefetch={() => {
        void refetch()
      }}
      inviteMembers={allocationMembers}
    >
      {({ handleContextMenu, contextMenuNode }) => (
        <div
          className="flex h-full flex-col"
          data-testid="email-reader-context-root"
          onContextMenu={(event) => handleContextMenu(event, email)}
        >
          {wholeHeaderEnabled ? (
            <FullReaderWholeHeader controller={controller} />
          ) : (
            <ReaderHeader controller={controller} />
          )}
          <ReaderContent controller={controller} />
          <ReaderOverlays controller={controller} />
          {contextMenuNode}
        </div>
      )}
    </EmailContextMenuProvider>
  )
}
